export const ROLE_VIEWER = 'viewer' as const;
export const ROLE_AUDITOR = 'auditor' as const;
export const ROLE_EDITOR = 'editor' as const;
export const ROLE_ADMIN = 'admin' as const;

export const ALL_ROLES = [ROLE_VIEWER, ROLE_AUDITOR, ROLE_EDITOR, ROLE_ADMIN] as const;

export type Role = (typeof ALL_ROLES)[number];

export const DEFAULT_ROLE: Role = ROLE_VIEWER;

export const ROLE_DEFINITION_COLLECTION = 'adminRoleDefinitions' as const;

export const ROLE_PRIVILEGE_KEYS = [
  'viewDashboards',
  'viewPortal',
  'editPortal',
  'editDocuments',
  'managePolicies',
  'manageRisks',
  'manageRiskRegister',
  'manageTreatmentPlans',
  'reviewRiskDecisions',
  'manageUsers',
  'manageRoles',
  'acknowledgePolicies',
  'manageAcknowledgements',
] as const;

export type RolePrivilegeKey = (typeof ROLE_PRIVILEGE_KEYS)[number];

export type RolePrivilegeFlags = Record<RolePrivilegeKey, boolean>;

export type RoleDefinitionAttributes = {
  name: string;
  description: string;
  priority: number;
  privileges: RolePrivilegeFlags;
};

export type RoleDefinitionSeed = RoleDefinitionAttributes & {
  id: string;
};

export type RoleDefinition = RoleDefinitionSeed & {
  createdAt?: string | null;
  updatedAt?: string | null;
};

export function createRolePrivilegeFlags(
  overrides: Partial<RolePrivilegeFlags> | null | undefined = {}
): RolePrivilegeFlags {
  const base: RolePrivilegeFlags = {
    viewDashboards: false,
    viewPortal: false,
    editPortal: false,
    editDocuments: false,
    managePolicies: false,
    manageRisks: false,
    manageRiskRegister: false,
    manageTreatmentPlans: false,
    reviewRiskDecisions: false,
    manageUsers: false,
    manageRoles: false,
    acknowledgePolicies: false,
    manageAcknowledgements: false,
  };
  return { ...base, ...(overrides ?? {}) };
}

export const ROLE_PRIVILEGE_INFO: Record<
  RolePrivilegeKey,
  { label: string; description: string }
> = {
  viewDashboards: {
    label: 'View dashboards',
    description: 'Access published dashboards and read-only policy content.',
  },
  viewPortal: {
    label: 'View portal',
    description: 'Read published policies and restricted portal content.',
  },
  editPortal: {
    label: 'Edit portal content',
    description: 'Edit portal snapshots, propose changes, and manage portal drafts.',
  },
  editDocuments: {
    label: 'Edit documents',
    description: 'Create or update governance records, policies, and workflows.',
  },
  managePolicies: {
    label: 'Manage policies',
    description: 'Create, update, and publish governance documents and related content.',
  },
  manageRisks: {
    label: 'Manage risks',
    description: 'Legacy flag covering all risk register privileges (register, treatments, and approvals).',
  },
  manageRiskRegister: {
    label: 'Manage risk register',
    description: 'Create, edit, and archive risks plus their linked assessments.',
  },
  manageTreatmentPlans: {
    label: 'Manage treatment plans',
    description: 'Create and update corrective action plans tied to risks.',
  },
  reviewRiskDecisions: {
    label: 'Review risk decisions',
    description: 'Approve or reject risks, assessments, and associated treatment plans.',
  },
  manageUsers: {
    label: 'Manage users',
    description: 'Invite, suspend, or update user profiles from the admin area.',
  },
  manageRoles: {
    label: 'Configure roles',
    description: 'Adjust role definitions and privilege mappings for the workspace.',
  },
  acknowledgePolicies: {
    label: 'Acknowledge policies',
    description: 'Attest to policy reviews directly from the portal.',
  },
  manageAcknowledgements: {
    label: 'Manage acknowledgements',
    description: 'View attestation dashboards and chase outstanding acknowledgements.',
  },
};

export const DEFAULT_ROLE_DEFINITIONS: readonly RoleDefinitionSeed[] = [
  {
    id: ROLE_VIEWER,
    name: 'Viewer',
    description: 'Read-only access to published content and dashboards.',
    priority: 0,
    privileges: createRolePrivilegeFlags({ viewDashboards: true, viewPortal: true, acknowledgePolicies: true }),
  },
  {
    id: ROLE_AUDITOR,
    name: 'Auditor',
    description: 'Read-only access with compliance dashboards and evidence visibility.',
    priority: 1,
    privileges: createRolePrivilegeFlags({ viewDashboards: true, viewPortal: true }),
  },
  {
    id: ROLE_EDITOR,
    name: 'Editor',
    description: 'Can edit governance content and manage daily workflows.',
    priority: 2,
    privileges: createRolePrivilegeFlags({
      viewDashboards: true,
      viewPortal: true,
      editPortal: true,
      editDocuments: true,
      managePolicies: true,
      manageRisks: true,
      manageRiskRegister: true,
      manageTreatmentPlans: true,
      reviewRiskDecisions: true,
      acknowledgePolicies: true,
      manageAcknowledgements: true,
    }),
  },
  {
    id: ROLE_ADMIN,
    name: 'Administrator',
    description: 'Full control, including role assignment and configuration.',
    priority: 3,
    privileges: createRolePrivilegeFlags({
      viewDashboards: true,
      viewPortal: true,
      editPortal: true,
      editDocuments: true,
      managePolicies: true,
      manageRisks: true,
      manageRiskRegister: true,
      manageTreatmentPlans: true,
      reviewRiskDecisions: true,
      manageUsers: true,
      manageRoles: true,
      acknowledgePolicies: true,
      manageAcknowledgements: true,
    }),
  },
] as const;

export function defaultPrivilegesForRole(role: Role | null): RolePrivilegeFlags {
  if (!role) return createRolePrivilegeFlags();
  const definition = DEFAULT_ROLE_DEFINITIONS.find((candidate) => candidate.id === role);
  if (!definition) return createRolePrivilegeFlags();
  return createRolePrivilegeFlags(definition.privileges);
}

const ROLE_PRIORITY: Record<Role, number> = {
  [ROLE_VIEWER]: 0,
  [ROLE_AUDITOR]: 1,
  [ROLE_EDITOR]: 2,
  [ROLE_ADMIN]: 3,
};

export const READ_ROLES: readonly Role[] = ALL_ROLES;
export const WRITE_ROLES: readonly Role[] = [ROLE_EDITOR, ROLE_ADMIN];

export function isRole(value: unknown): value is Role {
  return typeof value === 'string' && (ALL_ROLES as readonly string[]).includes(value as Role);
}

export function hasRequiredRole(
  role: Role | null | undefined,
  allowed: readonly Role[] | Role
): role is Role {
  if (!role) return false;
  const requiredList: Role[] = Array.isArray(allowed)
    ? [...allowed]
    : [allowed];
  return requiredList.some((candidate) => ROLE_PRIORITY[role] >= ROLE_PRIORITY[candidate]);
}

export function rolePriority(role: Role): number {
  return ROLE_PRIORITY[role];
}
